import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef, HostListener, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { Dialog, DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { FileUploadModule } from 'primeng/fileupload';
import { TabsModule } from 'primeng/tabs';
import { MenuModule } from 'primeng/menu';
import { MessageService, MenuItem } from 'primeng/api';
import {
  TaskCreateUpdate,
  TaskStatus,
  TaskTag,
  UserDetail,
  ProjectTask,
  TaskComment,
  TaskCommentCreate,
  TaskCommentAttachment,
  TaskAttribute,
  TaskAttributeValue,
  TaskAttributeOptionValue
} from '../../../models/task-management.models';
import { TaskManagementService } from '../../../services/task-management.service';
import { TimezoneService } from '../../../services/timezone.service';
import { UserService } from '../../../services/user.service';
import { SSOService } from '../../../services/sso.service';
import { ProjectFormService } from '../../../services/projectform.service';
import { ProjectFormModel } from '../../../models/projectform.model';
import { Subject, forkJoin, of, Observable } from 'rxjs';
import { takeUntil, catchError, map, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { MediaFallbackDirective } from '../../directives/media-fallback.directive';

@Component({
  selector: 'app-task-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    DialogModule,
    ToastModule,
    FileUploadModule,
    TabsModule,
    MenuModule,
    MediaFallbackDirective
  ],
  providers: [MessageService],
  template: `
    <p-dialog #taskDialog
      [(visible)]="visible"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [maximizable]="true"
      (onShow)="taskDialog.maximize()"
      styleClass="task-form-dialog"
      [style]="{'width': '1200px', 'max-width': '95vw', 'height': '95vh', 'max-height': '900px'}"
      (onHide)="onCancel()">

      <ng-template pTemplate="header">
        <div class="dialog-header-content">
          <span class="dialog-title">{{ isEditMode ? 'Edit Task' : 'Create New Task' }}</span>
          <div *ngIf="isEditMode && (editTask?.skidName || getTaskTemplateSubtitle() || editTask?.sectionName)" class="dialog-subtitle">
            <span *ngIf="editTask?.skidName">{{ editTask?.skidName }}</span>
            <span *ngIf="editTask?.skidName && (getTaskTemplateSubtitle() || editTask?.sectionName)" class="separator">|</span>
            <span *ngIf="getTaskTemplateSubtitle()">{{ getTaskTemplateSubtitle() }}</span>
            <span *ngIf="getTaskTemplateSubtitle() && editTask?.sectionName" class="separator">|</span>
            <span *ngIf="editTask?.sectionName">{{ editTask?.sectionName }}</span>
          </div>
        </div>
      </ng-template>

      <div class="task-form-container">
        <!-- Left Side: Basic Info & Comments -->
        <div class="left-panel">
          <div class="basic-fields">
            <form [formGroup]="taskForm">
              <div class="form-field">
                <div class="field-label-row">
                  <label for="title">Task Name</label>
                  <span class="req">*</span>
                </div>
                <input
                  id="title"
                  type="text"
                  class="field-input"
                  formControlName="title"
                  placeholder="Enter task name"
                  [readOnly]="readOnly"
                  [class.invalid]="taskForm.get('title')?.invalid && taskForm.get('title')?.touched" />
                <small class="error-msg" *ngIf="taskForm.get('title')?.invalid && taskForm.get('title')?.touched">
                  Task name is required
                </small>
              </div>
              <div class="form-field">
                <div class="field-label-row">
                  <label for="description">Description</label>
                </div>
                <div class="description-linkable-layout" [class.description-linkable-layout--single]="!shouldShowLinkablePanel">
                  <div class="description-panel">
                    <textarea
                      id="description"
                      class="field-textarea"
                      formControlName="description"
                      placeholder="Description here"
                      rows="6"
                      [readOnly]="readOnly">
                    </textarea>
                  </div>

                  <div *ngIf="shouldShowLinkablePanel" class="linkable-panel">
                    <div class="linkable-header">
                      <label>Linkable</label>
                    </div>

                    <div class="linkable-state" *ngIf="loadingProjectForms">
                      Loading linked forms...
                    </div>

                    <div class="linkable-state" *ngIf="!loadingProjectForms && projectForms.length === 0">
                      No linked forms found
                    </div>

                    <div class="linkable-list" *ngIf="!loadingProjectForms && projectForms.length > 0">
                      <button
                        type="button"
                        class="linkable-link"
                        *ngFor="let form of projectForms"
                        (click)="openProjectForm(form)">
                        {{ getLinkedFormLabel(form) }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

      <div class="panel-divider"></div>

      <!-- Comments Section (show in both create and edit mode) -->
      <div class="comments-section">
        <h4 class="section-heading">Comments & Activity</h4>

        <!-- Comment Input Row -->
        <div class="comment-input-row">
          <div class="comment-input-wrap">
            <input
              type="text"
              class="comment-text-input"
              [class.invalid]="commentValidationError"
              [(ngModel)]="newCommentText"
              placeholder="Write comment here"
              [readOnly]="readOnly"
              (ngModelChange)="onCommentTextChange()"
              (keydown.enter)="addComment()" />
            <button class="comment-attach-btn" type="button" [disabled]="readOnly" (click)="attachMenu.toggle($event)" title="Attach files">
              <i class="ri-add-circle-line"></i>
            </button>
          </div>
          <button class="btn-add-comment" type="button" (click)="addComment()" [disabled]="readOnly || !newCommentText.trim()">
            <i class="ri-add-line"></i>
            Add Comment
          </button>
        </div>

        <p-menu #attachMenu [model]="attachmentMenuItems" [popup]="true" appendTo="body" styleClass="project-card-menu"></p-menu>

        <!-- Selected Files Preview -->
        <div class="selected-files-preview" *ngIf="selectedCommentFiles?.length">
          <div class="preview-item" *ngFor="let file of selectedCommentFiles">
            <div class="image-preview" *ngIf="isImageFile(file)">
              <img [src]="getFilePreviewUrl(file)" [alt]="file.name" />
              <button class="remove-preview" (click)="removeCommentFile(file)" [disabled]="readOnly">
                <i class="ri-close-line"></i>
              </button>
            </div>
            <div class="file-preview" *ngIf="!isImageFile(file)">
              <i class="ri-file-line"></i>
              <span>{{ file.name }}</span>
              <button class="remove-preview-inline" (click)="removeCommentFile(file)" [disabled]="readOnly">
                <i class="ri-close-line"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="comment-validation-text" *ngIf="commentValidationError">
          Comment is required when adding a photo or attachment.
        </div>

        <!-- Comments List -->
        <div class="comments-list">
          <div class="comment-item" *ngFor="let comment of taskComments">
            <div class="comment-body">
              <div class="comment-header">
                <div class="comment-author-row">
                  <span class="comment-author">{{ comment.createdByName }}</span>
                  <span class="comment-action">commented</span>
                </div>
                <span class="comment-time">{{ formatCommentDate(comment.createdAt) }}</span>
              </div>
              <div class="comment-text">{{ comment.commentText }}</div>
              <div class="comment-attachments" *ngIf="comment.attachments?.length">
                <div *ngFor="let attachment of comment.attachments">
                  <div *ngIf="isImageFileType(attachment.fileType)" class="image-attachment">
                    <img [src]="attachment.thumbnailUrl || attachment.downloadUrl" [alt]="attachment.fileName"
                         [appMediaFallback]="attachment.thumbnailPath || attachment.fileUrl"
                         class="attachment-thumbnail" (click)="viewFullImage(attachment)"
                         loading="lazy" />
                  </div>
                  <div *ngIf="!isImageFileType(attachment.fileType)" class="file-attachment">
                    <i class="ri-file-line"></i>
                    <a [href]="attachment.downloadUrl" target="_blank">{{ attachment.fileName }}</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="no-comments" *ngIf="!taskComments?.length">
            <i class="ri-chat-3-line"></i>
            <p>No activity yet</p>
          </div>
        </div>
      </div>

    </div>

    <!-- Right Panel: Details Sidebar -->
    <div class="right-panel">
      <h3 class="details-heading">Details</h3>

      <div class="attributes-section">
        <div class="attribute-item" *ngFor="let attribute of allTaskAttributes">
          <div class="field-label-row">
            <i [class]="getAttributeIcon(attribute.attributeTypeName)"></i>
            <label [for]="'attr_' + attribute.attributeId">{{ attribute.attributeName }}</label>
            <span class="req" *ngIf="attribute.isRequired">*</span>
          </div>

          <!-- Type: Text -->
          <input
            *ngIf="attribute.attributeTypeName === 'Text'"
            [id]="'attr_' + attribute.attributeId"
            type="text"
            class="field-input"
            [(ngModel)]="attributeValues[attribute.attributeId]"
            [disabled]="readOnly"
            [placeholder]="'Enter ' + attribute.attributeName.toLowerCase()" />

          <!-- Type: Number -->
          <input
            *ngIf="attribute.attributeTypeName === 'Number'"
            [id]="'attr_' + attribute.attributeId"
            type="number"
            class="field-input"
            [(ngModel)]="attributeValues[attribute.attributeId]"
            [disabled]="readOnly"
            [placeholder]="'Enter ' + attribute.attributeName.toLowerCase()" />

          <!-- Type: Date -->
          <p-datepicker
            *ngIf="attribute.attributeTypeName === 'Date'"
            [id]="'attr_' + attribute.attributeId"
            [(ngModel)]="attributeValues[attribute.attributeId]"
            [placeholder]="'Select ' + attribute.attributeName.toLowerCase()"
            [disabled]="readOnly"
            [showClear]="!attribute.isRequired"
            styleClass="field-datepicker">
          </p-datepicker>

          <!-- Type: Status -->
          <p-select
            *ngIf="attribute.attributeTypeName === 'Status'"
            [id]="'attr_' + attribute.attributeId"
            [(ngModel)]="attributeValues[attribute.attributeId]"
            [options]="availableStatuses"
            optionLabel="statusName"
            optionValue="statusId"
            [placeholder]="'Select ' + attribute.attributeName.toLowerCase()"
            [showClear]="!attribute.isRequired"
            [disabled]="readOnly"
            appendTo="body"
            styleClass="field-select"
            [scrollHeight]="'300px'">
            <ng-template pTemplate="selectedItem">
              <div *ngIf="attributeValues[attribute.attributeId]" class="status-option">
                <span class="status-dot" [style.background-color]="getStatusColor(attributeValues[attribute.attributeId])"></span>
                {{ getStatusName(attributeValues[attribute.attributeId]) }}
              </div>
            </ng-template>
            <ng-template pTemplate="item" let-status>
              <div class="status-option">
                <span class="status-dot" [style.background-color]="status.color"></span>
                {{ status.statusName }}
              </div>
            </ng-template>
          </p-select>

          <!-- Type: User -->
          <div *ngIf="attribute.attributeTypeName === 'User'">
            <p-multiselect
              *ngIf="attribute.attributeName.toLowerCase().includes('watcher')"
              [id]="'attr_' + attribute.attributeId"
              [(ngModel)]="attributeValues[attribute.attributeId]"
              [options]="availableUsers"
              optionLabel="name"
              optionValue="userId"
              [placeholder]="'Select ' + attribute.attributeName.toLowerCase()"
              [showClear]="true"
              [disabled]="readOnly"
              appendTo="body"
              styleClass="field-select"
              [scrollHeight]="'300px'">
            </p-multiselect>
            <p-select
              *ngIf="!attribute.attributeName.toLowerCase().includes('watcher')"
              [id]="'attr_' + attribute.attributeId"
              [(ngModel)]="attributeValues[attribute.attributeId]"
              [options]="availableUsers"
              optionLabel="name"
              optionValue="userId"
              [placeholder]="'Select ' + attribute.attributeName.toLowerCase()"
              [showClear]="!attribute.isRequired"
              [disabled]="readOnly"
              appendTo="body"
              styleClass="field-select"
              [scrollHeight]="'300px'">
            </p-select>
          </div>

          <!-- Type: Dropdown -->
          <p-select
            *ngIf="attribute.attributeTypeName === 'Dropdown'"
            [id]="'attr_' + attribute.attributeId"
            [(ngModel)]="attributeValues[attribute.attributeId]"
            [options]="attributeOptions[attribute.attributeId]"
            optionLabel="text"
            optionValue="value"
            [placeholder]="'Select ' + attribute.attributeName.toLowerCase()"
            [showClear]="!attribute.isRequired"
            [disabled]="readOnly"
            appendTo="body"
            styleClass="field-select"
            [scrollHeight]="'300px'">
          </p-select>

          <!-- Type: Multiselect -->
          <p-multiselect
            *ngIf="attribute.attributeTypeName === 'Multiselect'"
            [id]="'attr_' + attribute.attributeId"
            [(ngModel)]="attributeValues[attribute.attributeId]"
            [options]="attributeOptions[attribute.attributeId]"
            optionLabel="text"
            optionValue="value"
            [placeholder]="'Select ' + attribute.attributeName.toLowerCase()"
            [showClear]="!attribute.isRequired"
            [disabled]="readOnly"
            appendTo="body"
            styleClass="field-select"
            [scrollHeight]="'300px'">
          </p-multiselect>

          <!-- Type: Boolean -->
          <div *ngIf="attribute.attributeTypeName === 'Boolean'" class="checkbox-field">
            <input
              [id]="'attr_' + attribute.attributeId"
              type="checkbox"
              [(ngModel)]="attributeValues[attribute.attributeId]"
              [disabled]="readOnly" />
            <label [for]="'attr_' + attribute.attributeId">{{ attribute.attributeName }}</label>
          </div>

          <!-- Type: Tags -->
          <div *ngIf="attribute.attributeTypeName === 'Tags'" class="tags-field-wrapper">
            <div class="tags-dropdown-trigger"
              (click)="toggleTagsDropdownForAttr(attribute.attributeId, $event)"
              [class.open]="isTagsDropdownOpen(attribute.attributeId)"
              [class.disabled]="readOnly">
              <div class="tags-pills-wrap">
                <span class="tag-pill" *ngFor="let tag of getSelectedTagObjects(attribute.attributeId)">
                  {{ tag.tagName }}
                  <button type="button" class="tag-pill-remove"
                    (click)="removeTagFromAttr(tag.tagId, attribute.attributeId, $event)"
                    [disabled]="readOnly">
                    <i class="ri-close-line"></i>
                  </button>
                </span>
                <span class="tags-placeholder" *ngIf="getSelectedTagObjects(attribute.attributeId).length === 0">
                  Select {{ attribute.attributeName.toLowerCase() }}
                </span>
              </div>
              <i class="ri-arrow-down-s-line tags-chevron"
                [class.rotated]="isTagsDropdownOpen(attribute.attributeId)"></i>
            </div>
            <div class="tags-dropdown-panel"
              *ngIf="isTagsDropdownOpen(attribute.attributeId)"
              (click)="$event.stopPropagation()">
              <div class="tags-search-row">
                <input
                  type="text"
                  class="tags-search-input"
                  [(ngModel)]="tagSearchByAttr[attribute.attributeId]"
                  placeholder="Search or create tag..."
                  (keydown.enter)="onTagSearchEnterForAttr(attribute.attributeId)" />
              </div>
              <div class="tags-list">
                <div class="tag-option"
                  *ngFor="let tag of getFilteredTagsForAttr(attribute.attributeId)"
                  (click)="toggleTagForAttr(tag.tagId, attribute.attributeId)">
                  <span class="custom-checkbox" [class.checked]="isTagSelectedForAttr(tag.tagId, attribute.attributeId)">
                    <i class="ri-check-line" *ngIf="isTagSelectedForAttr(tag.tagId, attribute.attributeId)"></i>
                  </span>
                  <span class="tag-option-label">{{ tag.tagName }}</span>
                </div>
                <div class="tags-list-empty"
                  *ngIf="getFilteredTagsForAttr(attribute.attributeId).length === 0 && !(tagSearchByAttr[attribute.attributeId] || '').trim()">
                  No tags available
                </div>
              </div>
              <div class="tag-create-hint" *ngIf="showCreateHintForAttr(attribute.attributeId)">
                <i class="pi pi-spin pi-spinner" *ngIf="isCreatingTag"></i>
                <span *ngIf="!isCreatingTag">
                  Press <kbd>Enter</kbd> to create "<strong>{{ (tagSearchByAttr[attribute.attributeId] || '').trim() }}</strong>"
                </span>
                <span *ngIf="isCreatingTag">Creating tag...</span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

  </div>

      <ng-template pTemplate="footer">
        <div class="form-actions">
          <div *ngIf="isEditMode && getLastUpdatedText() as lastUpdatedText" class="task-last-updated" aria-label="Last updated">
            <span class="task-last-updated__label">Last updated</span>
            <span>{{ lastUpdatedText }}</span>
          </div>
          <div class="form-actions__buttons">
          <button class="btn-cancel" type="button" (click)="onCancel()">Cancel</button>
          <button class="btn-submit" type="button" (click)="onSubmit()" [disabled]="readOnly || !taskForm.get('title')?.value?.trim()">
            <span *ngIf="loading" class="pi pi-spin pi-spinner"></span>
            {{ isEditMode ? 'Update Task' : 'Create Task' }}
          </button>
          </div>
        </div>
      </ng-template>
    </p-dialog>

    <p-toast />
  `,
  styleUrls: ['./task-form.component.css'],
})
export class TaskFormComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('taskDialog') taskDialog!: Dialog;
  @Input() visible: boolean = false;
  @Input() projectId!: number;
  @Input() blockScheduleId!: number;
  @Input() projectTemplateId?: number;
  @Input() sectionId?: number;
  @Input() availableStatuses: TaskStatus[] = [];
  @Input() availableTags: TaskTag[] = [];
  @Input() availableUsers: UserDetail[] = [];
  @Input() availableParentTasks: ProjectTask[] = [];
  @Input() editTask?: ProjectTask;
  @Input() defaultValues?: Partial<TaskCreateUpdate>;
  @Input() showParentTaskSelection: boolean = true;
  @Input() loading: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() isProjectFormMode: boolean = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() taskSubmit = new EventEmitter<TaskCreateUpdate>();
  @Output() cancel = new EventEmitter<void>();

  taskForm: FormGroup;
  isEditMode: boolean = false;
  
  // Comments properties
  taskComments: TaskComment[] = [];
  newCommentText: string = '';
  selectedCommentFiles: File[] = [];
  commentValidationError: boolean = false;
  attachmentMenuItems: MenuItem[] = [];
  selectedFilePreviewUrls: { file: File; previewUrl: string }[] = [];

  // Attributes properties
  taskAttributes: TaskAttribute[] = []; // All attributes (for backward compatibility)
  customTaskAttributes: TaskAttribute[] = []; // Custom attributes from database
  allTaskAttributes: TaskAttribute[] = []; // All attributes including system (sorted by sortOrder)
  attributeValues: { [attributeId: number]: any } = {};
  attributeOptions: { [attributeId: number]: { text: string, value: string }[] } = {};
  projectForms: ProjectFormModel[] = [];
  loadingProjectForms = false;
  

  
  // Tags dropdown state (per attribute)
  openTagsDropdownId: number | null = null;
  tagSearchByAttr: { [attrId: number]: string } = {};
  isCreatingTag = false;

  isTagsDropdownOpen(attributeId: number): boolean {
    return this.openTagsDropdownId === attributeId;
  }

  toggleTagsDropdownForAttr(attributeId: number, event: Event): void {
    if (this.readOnly) {
      return;
    }
    event.stopPropagation();
    if (this.openTagsDropdownId === attributeId) {
      this.openTagsDropdownId = null;
      this.tagSearchByAttr[attributeId] = '';
    } else {
      this.openTagsDropdownId = attributeId;
      if (!this.tagSearchByAttr[attributeId]) {
        this.tagSearchByAttr[attributeId] = '';
      }
    }
  }

  getSelectedTagIds(attributeId: number): number[] {
    const val = this.attributeValues[attributeId];
    return Array.isArray(val) ? val : [];
  }

  getSelectedTagObjects(attributeId: number): TaskTag[] {
    return this.getSelectedTagIds(attributeId)
      .map(id => this.availableTags.find(t => t.tagId === id))
      .filter((t): t is TaskTag => !!t);
  }

  getFilteredTagsForAttr(attributeId: number): TaskTag[] {
    const search = (this.tagSearchByAttr[attributeId] || '').trim().toLowerCase();
    if (!search) {
      return this.availableTags;
    }
    return this.availableTags.filter(t => t.tagName.toLowerCase().includes(search));
  }

  showCreateHintForAttr(attributeId: number): boolean {
    const search = (this.tagSearchByAttr[attributeId] || '').trim();
    if (!search) {
      return false;
    }
    return !this.availableTags.some(t => t.tagName.toLowerCase() === search.toLowerCase());
  }

  isTagSelectedForAttr(tagId: number, attributeId: number): boolean {
    return this.getSelectedTagIds(attributeId).includes(tagId);
  }

  toggleTagForAttr(tagId: number, attributeId: number): void {
    const current = [...this.getSelectedTagIds(attributeId)];
    const idx = current.indexOf(tagId);
    if (idx === -1) {
      current.push(tagId);
    } else {
      current.splice(idx, 1);
    }
    this.attributeValues[attributeId] = current;
  }

  removeTagFromAttr(tagId: number, attributeId: number, event: Event): void {
    event.stopPropagation();
    this.attributeValues[attributeId] = this.getSelectedTagIds(attributeId).filter(id => id !== tagId);
  }

  onTagSearchEnterForAttr(attributeId: number): void {
    const name = (this.tagSearchByAttr[attributeId] || '').trim();
    if (!name || this.isCreatingTag) {
      return;
    }
    const exactMatch = this.availableTags.find(t => t.tagName.toLowerCase() === name.toLowerCase());
    if (exactMatch) {
      if (!this.isTagSelectedForAttr(exactMatch.tagId, attributeId)) {
        this.toggleTagForAttr(exactMatch.tagId, attributeId);
      }
      this.tagSearchByAttr[attributeId] = '';
      return;
    }
    this.isCreatingTag = true;
    this.taskManagementService.createTaskTag({ tagName: name })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (newTag) => {
          this.availableTags = [...this.availableTags, newTag];
          const current = [...this.getSelectedTagIds(attributeId), newTag.tagId];
          this.attributeValues[attributeId] = current;
          this.tagSearchByAttr[attributeId] = '';
          this.isCreatingTag = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error creating tag:', err);
          this.isCreatingTag = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to create tag. Please try again.'
          });
        }
      });
  }

  @HostListener('document:click')
  closeTagsDropdown(): void {
    if (this.openTagsDropdownId !== null) {
      this.tagSearchByAttr[this.openTagsDropdownId] = '';
      this.openTagsDropdownId = null;
    }
  }

  private destroy$ = new Subject<void>();

  constructor(
      private fb: FormBuilder,
      private messageService: MessageService,
      private taskManagementService: TaskManagementService,
      private timezoneService: TimezoneService,
      private userService: UserService,
      private ssoService: SSOService,
      private projectFormService: ProjectFormService,
      private router: Router,
      private cdr: ChangeDetectorRef
  ) {
    this.taskForm = this.createForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle when editTask input changes (when opening a different task for editing)
    if (changes['editTask'] && !changes['editTask'].firstChange) {
      console.log('EditTask changed:', changes['editTask'].currentValue);
      this.isEditMode = !!changes['editTask'].currentValue;
      
      if (this.isEditMode && changes['editTask'].currentValue) {
        // Reset form and reload data for the new task
        this.resetAttributeValues();
        this.populateFormFromTask(changes['editTask'].currentValue);

        // Sync form values to attributeValues synchronously before CD runs
        if (this.allTaskAttributes.length > 0) {
          this.initializeAttributeValues();
          // Load full attribute values from API (will overwrite with complete data)
          this.loadExistingAttributeValues();
        }
        // If attributes not loaded yet, they will be loaded in ngOnInit
        this.loadTaskComments();
      } else {
        // Switched to create mode or task cleared
        this.resetAttributeValues();
        this.resetForm();
      }
    }

    // Handle when defaultValues input changes (for create mode)
    if (changes['defaultValues'] && !changes['defaultValues'].firstChange && !this.isEditMode) {
      console.log('DefaultValues changed for create mode:', changes['defaultValues'].currentValue);
      if (changes['defaultValues'].currentValue) {
        this.taskForm.patchValue(changes['defaultValues'].currentValue);
      }
    }

    if ((changes['visible'] || changes['isProjectFormMode'] || changes['projectId']) && this.visible) {
      this.loadProjectFormsForLinkable();
    }
  }

  ngOnInit() {
    console.log('TaskForm ngOnInit - inputs:', {
      projectId: this.projectId,
      blockScheduleId: this.blockScheduleId,
      projectTemplateId: this.projectTemplateId,
      editTask: this.editTask,
      defaultValues: this.defaultValues
    });

    this.isEditMode = !!this.editTask;

    // Initialize attachment menu
    this.attachmentMenuItems = [
      {
        label: 'Take Photo',
        icon: 'pi pi-camera',
        command: () => this.openCameraCapture()
      },
      {
        label: 'Attach File or Photo',
        icon: 'pi pi-paperclip',
        command: () => this.openFileSelector()
      }
    ];
    
    if (this.editTask) {
      this.populateFormFromTask(this.editTask);
    } else if (this.defaultValues) {
      console.log('Patching with defaultValues:', this.defaultValues);
      this.taskForm.patchValue(this.defaultValues);
    }

    // Set default values (only set if they have valid values)
    if (this.projectId) {
      console.log('Setting projectId:', this.projectId);
      this.taskForm.patchValue({ projectId: this.projectId });
    }
    
    // Always set blockScheduleId, use 0 for project-level tasks
    const blockScheduleIdValue = (this.blockScheduleId && this.blockScheduleId > 0) ? this.blockScheduleId : 0;
    console.log('Setting blockScheduleId:', blockScheduleIdValue);
    this.taskForm.patchValue({ blockScheduleId: blockScheduleIdValue });
    
    if (this.projectTemplateId) {
      console.log('Setting projectTemplateId:', this.projectTemplateId);
      this.taskForm.patchValue({ projectTemplateId: this.projectTemplateId });
    }

    if (this.sectionId) {
      console.log('Setting sectionId:', this.sectionId);
      this.taskForm.patchValue({ sectionId: this.sectionId });
    }

    // Set default status if available and not editing
    if (!this.isEditMode && this.availableStatuses.length > 0) {
      const defaultStatus = this.availableStatuses.find(s => s.statusType === 'new') || this.availableStatuses[0];
      console.log('Setting default status:', defaultStatus);
      this.taskForm.patchValue({ statusId: defaultStatus.statusId });
    }

    console.log('Form values after init:', this.taskForm.value);
    console.log('Form validity:', this.taskForm.valid);
    console.log('Form errors:', this.getFormErrors());

    // Load lookup data if not provided via inputs
    this.loadLookupDataIfNeeded();
    this.loadProjectFormsForLinkable();

    // Load task attributes and comments
    this.loadTaskAttributes();
    if (this.isEditMode) {
      this.loadTaskComments();
      // loadExistingAttributeValues() will be called after attributes are loaded
    }
  }

  get shouldShowLinkablePanel(): boolean {
    return this.isEditMode && (this.loadingProjectForms || this.projectForms.length > 0);
  }

  private loadProjectFormsForLinkable(): void {
    if (!this.visible || !this.projectId || !this.isEditMode || !this.editTask?.taskId) {
      this.projectForms = [];
      this.loadingProjectForms = false;
      return;
    }

    this.loadingProjectForms = true;
    this.projectFormService.getLinkedProjectFormsByTaskId(this.projectId, this.editTask.taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (forms) => {
          this.projectForms = (forms || []).sort((left, right) =>
            String(left.name || left.id).localeCompare(String(right.name || right.id))
          );
          this.loadingProjectForms = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading linked project forms:', error);
          this.projectForms = [];
          this.loadingProjectForms = false;
          this.cdr.markForCheck();
        }
      });
  }

  openProjectForm(form: ProjectFormModel): void {
    if (!this.projectId || !form?.id) {
      return;
    }

    const normalizedAppName = (environment.appName || '').replace(/^\/+|\/+$/g, '');
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/', normalizedAppName, 'form-checksheet', this.projectId, form.id])
    );
    window.open(url, '_blank', 'noopener');
  }

  getLinkedFormLabel(form: ProjectFormModel): string {
    const formName = (form.name || '').trim() || `Form #${form.id}`;
    const shortCode = (form.shortCode || '').trim();

    return shortCode ? `${shortCode} - ${formName}` : formName;
  }

  getTaskTemplateSubtitle(): string {
    return (this.editTask?.templateShortCode || this.editTask?.templateName || '').trim();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createForm(): FormGroup {
    return this.fb.group({
      projectId: [this.projectId || null, Validators.required],
      blockScheduleId: [this.blockScheduleId || 0], // Use 0 instead of null
      projectTemplateId: [this.projectTemplateId || null],
      sectionId: [this.sectionId || null],
      title: ['', Validators.required],
      description: [''],
      statusId: [null], // Optional - can be set via attributes
      assignedTo: [null],
      parentTaskId: [null],
      startDate: [null],
      endDate: [null],
      tagIds: [[]]
    });
  }

  private populateFormFromTask(task: ProjectTask) {
    this.taskForm.patchValue({
      projectId: task.projectId,
      blockScheduleId: task.blockScheduleId,
      projectTemplateId: task.projectTemplateId,
      title: task.title,
      description: task.description,
      statusId: task.statusId,
      assignedTo: task.assignedTo,
      parentTaskId: task.parentTaskId,
      startDate: this.timezoneService.parseStoredDateToPickerDate(task.startDate),
      endDate: this.timezoneService.parseStoredDateToPickerDate(task.endDate),
      tagIds: task.tags?.map(t => t.tagId) || []
    });
  }

  onSubmit() {
    if (this.readOnly) {
      return;
    }
    if (this.hasPendingCommentDraft() && !this.isEditMode) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Save Task First',
        detail: 'Please save the task before adding comments or attachments.'
      });
      return;
    }
    if (this.requiresCommentForAttachments()) {
      this.commentValidationError = true;
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation',
        detail: 'Comment is required when adding a photo or attachment.'
      });
      return;
    }
    // Check if title is provided (only required field)
    const title = this.taskForm.get('title')?.value?.trim();
    if (!title) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Task name is required.'
      });
      return;
    }

    // Map attribute values back to form controls before submission
    this.mapAttributeValuesToForm();

    // Sync tagIds from Tags-type attribute so the form control reflects the current selection
    const tagsAttr = this.allTaskAttributes.find(a => a.attributeTypeName === 'Tags');
    if (tagsAttr) {
      const tagsValue = this.attributeValues[tagsAttr.attributeId];
      this.taskForm.patchValue({ tagIds: Array.isArray(tagsValue) ? tagsValue : [] });
    }

    const formValue = this.taskForm.value;

    // If statusId is not set, try to use the first available status as default (for create mode)
    if (!formValue.statusId && this.availableStatuses?.length > 0 && !this.isEditMode) {
      const defaultStatus = this.availableStatuses.find(s => s.statusType === 'new') || this.availableStatuses[0];
      formValue.statusId = defaultStatus.statusId;
    }

    // Collect attribute values (all attributes for both create and update)
    const attributes = this.collectAttributeValues();

    // Extract watcher user IDs from watcher attributes for the WatcherUserIds property
    const watcherUserIds = this.extractWatcherUserIds();

    console.log('Submitting task with attributes:', attributes); // Debug log
    console.log('Current attributeValues object:', this.attributeValues); // Debug log
    console.log('Extracted watcher user IDs:', watcherUserIds); // Debug log
    console.log('Form value before creating taskData:', formValue); // Debug log

    const taskData: TaskCreateUpdate = {
      ...formValue,
      startDate: formValue.startDate instanceof Date ? this.timezoneService.buildDateTimestamp(formValue.startDate) : formValue.startDate,
      endDate: formValue.endDate instanceof Date ? this.timezoneService.buildDateTimestamp(formValue.endDate) : formValue.endDate,
      isActive: true,
      attributes: attributes,
      watcherUserIds: watcherUserIds // Add watchers for backward compatibility
    };

    // For update mode, ensure we have the correct projectId
    if (this.isEditMode && this.editTask) {
      // Ensure projectId is set from the original task if not in form
      if (!taskData.projectId && this.editTask.projectId) {
        taskData.projectId = this.editTask.projectId;
        console.log('Setting projectId from editTask:', this.editTask.projectId);
      }
      console.log('Submitting task update for taskId:', this.editTask.taskId);
    } else {
      // For create mode, ensure projectId is set from input
      if (!taskData.projectId && this.projectId) {
        taskData.projectId = this.projectId;
        console.log('Setting projectId from input:', this.projectId);
      }
      console.log('Submitting new task creation');
    }

    console.log('Final taskData being submitted:', taskData); // Debug log
    this.submitPendingCommentDraft(false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (canContinue) => {
          if (canContinue) {
            this.taskSubmit.emit(taskData);
          }
        },
        error: (error) => {
          console.error('Error saving pending comment before task submit:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save pending comment before updating task.'
          });
        }
      });
  }

  private mapAttributeValuesToForm() {
    // Map attribute values back to form controls for system attributes only
    this.allTaskAttributes.forEach(attr => {
      if (!attr.isSystem) return; // Only map system attributes to form

      const value = this.attributeValues[attr.attributeId];
      const attrType = attr.attributeTypeName;
      const attrName = attr.attributeName.toLowerCase();

      if (value !== undefined && value !== null) {
        if (attrType === 'Status') {
          this.taskForm.patchValue({ statusId: value });
        } else if (attrType === 'User') {
          if (!attrName.includes('watcher')) {
            this.taskForm.patchValue({ assignedTo: value });
          }
          // Watcher attributes are handled by the dynamic UI only, no form control sync needed
        } else if (attrType === 'Date') {
          if (attrName.includes('start')) {
            this.taskForm.patchValue({ startDate: value });
          } else if (attrName.includes('end') || attrName.includes('due')) {
            this.taskForm.patchValue({ endDate: value });
          }
        }
      }
    });
  }

  private collectAttributeValues() {
    const attributes: { attributeId: number; attributeValue?: string }[] = [];

    // Iterate through ALL attributes that have values in attributeValues
    // In edit mode, include all attributes to capture changes
    // In create mode, include both system and custom attributes
    Object.keys(this.attributeValues).forEach(attributeIdStr => {
      const attributeId = parseInt(attributeIdStr);
      const value = this.attributeValues[attributeId];

      // Find the attribute definition to get its type
      const attribute = this.allTaskAttributes.find(attr => attr.attributeId === attributeId);
      if (!attribute) return; // Skip if attribute definition not found

      // Only include attributes that have values
      if (value !== undefined && value !== null && value !== '') {
        let attributeValue: string | undefined;

        // Handle different attribute types
        switch (attribute.attributeTypeName) {
          case 'Boolean':
            attributeValue = value ? 'true' : 'false';
            break;
          case 'Date':
            attributeValue = value instanceof Date ? this.timezoneService.buildDateTimestamp(value) : value;
            break;
          case 'Multiselect':
            // Convert array to JSON string for multiselect (including watchers)
            attributeValue = Array.isArray(value) ? JSON.stringify(value) : value;
            break;
          case 'Tags':
            // Convert array of tag IDs to JSON string
            attributeValue = Array.isArray(value) ? JSON.stringify(value) : value;
            break;
          case 'User':
            // Handle both single user and multi-user (watcher) attributes
            if (attribute.attributeName.toLowerCase().includes('watcher')) {
              // For watcher attributes, convert array to JSON
              attributeValue = Array.isArray(value) ? JSON.stringify(value) : value;
            } else {
              // For single user attributes, convert to string
              attributeValue = String(value);
            }
            break;
          case 'Number':
          case 'Text':
          case 'Dropdown':
          case 'Status':
          default:
            attributeValue = String(value);
            break;
        }

        attributes.push({
          attributeId: attributeId,
          attributeValue: attributeValue
        });
      }
    });

    console.log('Collected attributes:', attributes); // Debug log
    return attributes;
  }

  private extractWatcherUserIds(): number[] {
    // Find all watcher attributes and collect their user IDs
    const watcherUserIds: number[] = [];
    
    this.allTaskAttributes.forEach(attr => {
      if (attr.attributeTypeName === 'User' && attr.attributeName.toLowerCase().includes('watcher')) {
        const value = this.attributeValues[attr.attributeId];
        if (Array.isArray(value)) {
          // Value should be an array of user IDs
          value.forEach(userId => {
            const userIdNum = typeof userId === 'number' ? userId : parseInt(userId);
            if (!isNaN(userIdNum) && !watcherUserIds.includes(userIdNum)) {
              watcherUserIds.push(userIdNum);
            }
          });
        } else if (value && !isNaN(parseInt(String(value)))) {
          // Handle single value case
          const userIdNum = parseInt(String(value));
          if (!watcherUserIds.includes(userIdNum)) {
            watcherUserIds.push(userIdNum);
          }
        }
      }
    });

    console.log('Extracted watcher user IDs for', this.isEditMode ? 'update' : 'create', ':', watcherUserIds);
    return watcherUserIds;
  }

  onCancel() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.cancel.emit();
    this.resetForm();
  }

  private resetAttributeValues() {
    this.attributeValues = {};
    console.log('Attribute values reset');
  }

  private resetForm() {
    this.taskForm.reset();
    this.isEditMode = false;
    this.openTagsDropdownId = null;
    this.tagSearchByAttr = {};

    // Reset comments and attachments
    this.taskComments = [];
    this.newCommentText = '';
    this.selectedCommentFiles = [];

    // Clean up file preview URLs to prevent memory leaks
    this.selectedFilePreviewUrls.forEach(preview => {
      URL.revokeObjectURL(preview.previewUrl);
    });
    this.selectedFilePreviewUrls = [];

    // Reset attribute values
    this.attributeValues = {};

    // Reset to default values from inputs and defaultValues
    if (this.defaultValues) {
      this.taskForm.patchValue(this.defaultValues);
    }

    if (this.projectId) {
      this.taskForm.patchValue({ projectId: this.projectId });
    }

    if (this.blockScheduleId) {
      this.taskForm.patchValue({ blockScheduleId: this.blockScheduleId });
    }

    if (this.projectTemplateId) {
      this.taskForm.patchValue({ projectTemplateId: this.projectTemplateId });
    }

    if (this.sectionId) {
      this.taskForm.patchValue({ sectionId: this.sectionId });
    }
  }

  private markFormGroupTouched() {
    Object.keys(this.taskForm.controls).forEach(key => {
      this.taskForm.get(key)?.markAsTouched();
    });
  }

  private getFormErrors() {
    const errors: any = {};
    Object.keys(this.taskForm.controls).forEach(key => {
      const control = this.taskForm.get(key);
      if (control && control.errors) {
        errors[key] = {
          value: control.value,
          errors: control.errors
        };
      }
    });
    return errors;
  }

  getStatusName(statusId: number): string {
    const status = this.availableStatuses.find(s => s.statusId === statusId);
    return status?.statusName || '';
  }

  getStatusColor(statusId: number): string {
    const status = this.availableStatuses.find(s => s.statusId === statusId);
    return status?.color || '#6c757d';
  }

  getFormErrorsList(): string[] {
    const errors: string[] = [];
    Object.keys(this.taskForm.controls).forEach(key => {
      const control = this.taskForm.get(key);
      if (control && control.errors) {
        Object.keys(control.errors).forEach(errorKey => {
          errors.push(`${key}: ${errorKey} (value: "${control.value}")`);
        });
      }
    });
    return errors;
  }

  getFormDebugInfo(): string {
    return JSON.stringify(this.taskForm.value, null, 2);
  }

  // Comments Methods
  onCommentFilesSelect(event: any) {
    if (this.readOnly) {
      return;
    }
    if (event.files) {
      this.selectedCommentFiles = Array.from(event.files);
    }
  }

  removeCommentFile(fileToRemove: File) {
    if (this.readOnly) {
      return;
    }
    this.selectedCommentFiles = this.selectedCommentFiles.filter(file => file !== fileToRemove);
    if (this.selectedCommentFiles.length === 0) {
      this.commentValidationError = false;
    }
    // Also remove preview URL
    const previewIndex = this.selectedFilePreviewUrls.findIndex(p => p.file === fileToRemove);
    if (previewIndex !== -1) {
      URL.revokeObjectURL(this.selectedFilePreviewUrls[previewIndex].previewUrl);
      this.selectedFilePreviewUrls.splice(previewIndex, 1);
    }
  }

  openCameraCapture() {
    if (this.readOnly) {
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Use rear camera
    input.onchange = (event: any) => {
      if (event.target.files && event.target.files.length > 0) {
        this.handleFileSelection(Array.from(event.target.files));
      }
    };
    input.click();
  }

  openFileSelector() {
    if (this.readOnly) {
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,application/pdf,.doc,.docx,.txt';
    input.multiple = true;
    input.onchange = (event: any) => {
      if (event.target.files && event.target.files.length > 0) {
        this.handleFileSelection(Array.from(event.target.files));
      }
    };
    input.click();
  }

  handleFileSelection(files: File[]) {
    if (this.readOnly) {
      return;
    }
    files.forEach(file => {
      this.selectedCommentFiles.push(file);
      this.commentValidationError = !this.newCommentText.trim();

      // Create preview URL for images
      if (file.type.startsWith('image/')) {
        const previewUrl = URL.createObjectURL(file);
        this.selectedFilePreviewUrls.push({ file, previewUrl });
      }
    });

    this.messageService.add({
      severity: 'success',
      summary: 'Files Selected',
      detail: `${files.length} file(s) selected successfully.`
    });
  }

  onCommentTextChange() {
    if (this.newCommentText.trim()) {
      this.commentValidationError = false;
    } else if (this.selectedCommentFiles.length > 0) {
      this.commentValidationError = true;
    }
  }

  getFilePreviewUrl(file: File): string | null {
    const preview = this.selectedFilePreviewUrls.find(p => p.file === file);
    return preview ? preview.previewUrl : null;
  }

  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  isImageFileType(fileType?: string): boolean {
    return fileType ? fileType.startsWith('image/') : false;
  }

  viewFullImage(attachment: TaskCommentAttachment) {
    if (attachment.downloadUrl) {
      window.open(attachment.downloadUrl, '_blank');
    }
  }

  onImageError(event: any) {
    console.error('Image failed to load:', event.target.src);
    // Optionally, you could set a default image or hide the image
    event.target.style.display = 'none';
  }

  addComment() {
    if (this.readOnly) {
      return;
    }
    this.submitPendingCommentDraft(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {},
        error: () => {}
      });
  }

  formatCommentDate(date: Date): string {
    const commentDate = new Date(date);
    return commentDate.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  getLastUpdatedText(): string | null {
    if (!this.editTask?.updatedAt) {
      return null;
    }

    const updatedBy = this.editTask.updatedByName?.trim() || 'Unknown user';
    return `${updatedBy} on ${this.formatAuditDateTime(this.editTask.updatedAt)}`;
  }

  private formatAuditDateTime(value: string | Date): string {
    const rawValue = String(value);
    const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(rawValue);
    const date = new Date(value instanceof Date ? value : (hasTimezone ? rawValue : `${rawValue}Z`));

    if (Number.isNaN(date.getTime())) {
      return rawValue;
    }

    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  // Load lookup data (statuses, users, tags) if not provided via inputs
  private loadLookupDataIfNeeded(): void {
    const requests: { statuses?: boolean; users?: boolean; tags?: boolean } = {};

    // Check which data needs to be loaded
    if (this.availableStatuses.length === 0) {
      requests.statuses = true;
    }
    if (this.availableUsers.length === 0) {
      requests.users = true;
    }
    if (this.availableTags.length === 0) {
      requests.tags = true;
    }

    // If all data is already provided, skip
    if (!requests.statuses && !requests.users && !requests.tags) {
      console.log('All lookup data provided via inputs, skipping API calls');
      return;
    }

    console.log('Loading lookup data from APIs:', requests);

    // Load statuses if needed
    if (requests.statuses) {
      this.taskManagementService.getAllTaskStatuses()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (statuses) => {
            // Skip if parent already provided data via @Input
            if (this.availableStatuses.length > 0) return;
            this.availableStatuses = statuses;
            console.log('Loaded statuses:', statuses.length);
            // Set default status for create mode
            if (!this.isEditMode && statuses.length > 0) {
              const defaultStatus = statuses.find(s => s.statusType === 'new') || statuses[0];
              this.taskForm.patchValue({ statusId: defaultStatus.statusId });
            }
            this.cdr.markForCheck();
          },
          error: (err) => console.error('Error loading statuses:', err)
        });
    }

    // Load users if needed
    if (requests.users) {
      const usersRequest = this.projectId && this.projectId > 0
        ? this.userService.getProjectUserList(this.projectId)
        : this.userService.getUserList();

      usersRequest
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (users) => {
            // Skip if parent already provided data via @Input
            if (this.availableUsers.length > 0) return;
            // Map UserModel to UserDetail format expected by the form
            this.availableUsers = users
              .filter(u => u.userId !== undefined && u.isActive !== false)
              .map(u => ({
                userId: u.userId as number,
                name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Unknown',
                email: u.email
              }));
            console.log('Loaded users:', this.availableUsers.length);
            this.cdr.markForCheck();
          },
          error: (err) => console.error('Error loading users:', err)
        });
    }

    // Load tags if needed
    if (requests.tags) {
      this.taskManagementService.getAllTaskTags()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (tags) => {
            // Skip if parent already provided data via @Input
            if (this.availableTags.length > 0) return;
            this.availableTags = tags;
            console.log('Loaded tags:', tags.length);
            this.cdr.markForCheck();
          },
          error: (err) => console.error('Error loading tags:', err)
        });
    }
  }

  // Attributes Methods
  getAttributeOptions(attributeId: number): { text: string, value: string }[] {
    return this.attributeOptions[attributeId] || [];
  }

  loadTaskAttributes() {
    console.log('loadTaskAttributes() called');
    // Load all active task attributes from API
    this.taskManagementService.getAllTaskAttributes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (attributes) => {
          // Filter to only active and displayed attributes, then sort by sortOrder
          const activeAttributes = attributes
            .filter(attr => attr.isActive && attr.isDisplayed)
            .sort((a, b) => a.sortOrder - b.sortOrder);

          // Store ALL attributes (system + custom) for dynamic rendering
          this.allTaskAttributes = activeAttributes;

          // Filter to only custom (non-system) attributes for backward compatibility
          this.customTaskAttributes = activeAttributes.filter(attr => !attr.isSystem);
          this.taskAttributes = activeAttributes;

          // Initialize attribute values from form or existing task
          this.initializeAttributeValues();

          // Load dropdown options for each dropdown/multiselect attribute
          this.loadDropdownOptions();

          // Load existing attribute values after attributes are loaded (for edit mode)
          if (this.isEditMode) {
            this.loadExistingAttributeValues();
          }
        },
        error: (error) => {
          console.error('Error loading task attributes:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load task attributes.'
          });
        }
      });
  }

  loadDropdownOptions() {
    // Load options for dropdown and multiselect attributes
    const dropdownAttributes = this.allTaskAttributes.filter(
      attr => attr.attributeTypeName === 'Dropdown' || attr.attributeTypeName === 'Multiselect'
    );

    if (dropdownAttributes.length === 0) return;

    // Pre-initialize empty arrays so template bindings have stable references
    dropdownAttributes.forEach(attr => {
      if (!this.attributeOptions[attr.attributeId]) {
        this.attributeOptions[attr.attributeId] = [];
      }
    });

    // Use forkJoin to load all options atomically, preventing NG0100
    const optionRequests = dropdownAttributes.map(attr =>
      this.taskManagementService.getTaskAttributeOptions(attr.attributeId).pipe(
        catchError(error => {
          console.error(`Error loading options for attribute ${attr.attributeId}:`, error);
          return of([] as TaskAttributeOptionValue[]);
        })
      )
    );

    forkJoin(optionRequests)
      .pipe(takeUntil(this.destroy$))
      .subscribe(results => {
        // Update all options at once in a single callback
        dropdownAttributes.forEach((attr, index) => {
          this.attributeOptions[attr.attributeId] = results[index].map(opt => ({
            text: opt.valueText,
            value: opt.valueText
          }));
        });
        this.cdr.markForCheck();
      });
  }

  initializeAttributeValues() {
    // Initialize attribute values from form controls for system attributes
    this.allTaskAttributes.forEach(attr => {
      if (!attr.isSystem) return; // Only initialize system attributes from form

      const attrType = attr.attributeTypeName;
      const attrName = attr.attributeName.toLowerCase();

      // Map form control values to attributeValues based on type and name pattern
      if (attrType === 'Status' && this.taskForm.get('statusId')?.value) {
        this.attributeValues[attr.attributeId] = this.taskForm.get('statusId')?.value;
      } else if (attrType === 'User') {
        if (this.taskForm.get('assignedTo')?.value && !attrName.includes('watcher')) {
          this.attributeValues[attr.attributeId] = this.taskForm.get('assignedTo')?.value;
        }
      } else if (attrType === 'Date') {
        if (attrName.includes('start') && this.taskForm.get('startDate')?.value) {
          this.attributeValues[attr.attributeId] = this.taskForm.get('startDate')?.value;
        } else if ((attrName.includes('end') || attrName.includes('due')) && this.taskForm.get('endDate')?.value) {
          this.attributeValues[attr.attributeId] = this.taskForm.get('endDate')?.value;
        }
      }
    });
  }

  getAttributeIcon(attributeTypeName: string): string {
    // Return icon based on attribute TYPE (not name)
    const iconMap: { [key: string]: string } = {
      'Status': 'ri-checkbox-circle-line',
      'Text': 'ri-text',
      'Number': 'ri-hashtag',
      'Date': 'ri-calendar-line',
      'User': 'ri-user-line',
      'Dropdown': 'ri-list-check',
      'Multiselect': 'ri-checkbox-multiple-line',
      'Boolean': 'ri-checkbox-circle-line',
      'Tags': 'ri-price-tag-3-line'
    };

    return iconMap[attributeTypeName] || 'ri-question-line';
  }

  loadTaskComments() {
    // Only load comments if editing an existing task
    if (!this.isEditMode || !this.editTask?.taskId) {
      console.log('loadTaskComments() skipped - not in edit mode or no taskId');
      this.setTaskComments([]);
      return;
    }

    console.log('loadTaskComments() called for taskId:', this.editTask.taskId);
    
    this.taskManagementService.getTaskComments(this.editTask.taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (comments) => {
          // Sort comments by createdAt in descending order (latest first)
          const sortedComments = (comments || []).sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          this.setTaskComments(sortedComments);
          console.log('Loaded task comments with attachments:', sortedComments);
          // Debug attachment data
          sortedComments.forEach(comment => {
            if (comment.attachments?.length) {
              console.log('Comment attachments:', comment.attachments.map(a => ({
                fileName: a.fileName,
                fileType: a.fileType,
                thumbnailUrl: a.thumbnailUrl,
                downloadUrl: a.downloadUrl,
                isImage: this.isImageFileType(a.fileType)
              })));
            }
          });
        },
        error: (error) => {
          console.error('Error loading task comments:', error);
          this.setTaskComments([]);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load comments. Please refresh and try again.'
          });
        }
      });
  }

  private setTaskComments(comments: TaskComment[]): void {
    setTimeout(() => {
      this.taskComments = comments;
      this.cdr.markForCheck();
    });
  }





  loadExistingAttributeValues() {
    if (!this.isEditMode || !this.editTask?.taskId) {
      console.log('loadExistingAttributeValues() skipped - not in edit mode or no taskId');
      return;
    }
    
    console.log('loadExistingAttributeValues() called for taskId:', this.editTask.taskId);
    
    // Load existing attribute values from API
    this.taskManagementService.getTaskAttributeValues(this.editTask.taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (attributeValues: TaskAttributeValue[]) => {
          console.log('Loaded existing attribute values:', attributeValues);
          
          // Reset attribute values
          this.attributeValues = {};

          // Populate attributeValues object
          attributeValues.forEach(attrValue => {
            const attribute = this.allTaskAttributes.find(attr => attr.attributeId === attrValue.attributeId);
            if (!attribute) return;

            let value: any = attrValue.attributeValue;

            // Parse values based on attribute type
            switch (attribute.attributeTypeName) {
              case 'Boolean':
                value = value === 'true';
                break;
              case 'Date':
                value = this.timezoneService.parseStoredDateToPickerDate(value);
                break;
              case 'Multiselect':
                value = this.parseMultiSelectAttributeValue(value);
                break;
              case 'Tags':
                // Parse JSON array of tag IDs
                try {
                  value = JSON.parse(value);
                } catch {
                  value = [];
                }
                break;
              case 'Number':
                value = value ? parseFloat(value) : null;
                break;
              case 'User':
                // Handle both single user and multi-user (watcher) attributes
                if (attribute.attributeName.toLowerCase().includes('watcher')) {
                  // For watcher attributes, parse JSON array (same as save logic)
                  try {
                    value = JSON.parse(value);
                  } catch {
                    value = [];
                  }
                } else {
                  // For single user attributes, parse as integer (existing behavior)
                  value = value ? parseInt(value) : null;
                }
                break;
              case 'Status':
                value = value ? parseInt(value) : null;
                break;
              case 'Text':
              case 'Dropdown':
              default:
                // Keep as string
                break;
            }

            this.attributeValues[attrValue.attributeId] = value;
          });

          this.allTaskAttributes.forEach(attribute => {
            const attrName = attribute.attributeName.toLowerCase();
            if (attribute.attributeTypeName !== 'Date' || this.attributeValues[attribute.attributeId]) {
              return;
            }

            if (attrName.includes('start') && this.editTask?.startDate) {
              this.attributeValues[attribute.attributeId] = this.timezoneService.parseStoredDateToPickerDate(this.editTask.startDate);
            } else if ((attrName.includes('end') || attrName.includes('due')) && this.editTask?.endDate) {
              this.attributeValues[attribute.attributeId] = this.timezoneService.parseStoredDateToPickerDate(this.editTask.endDate);
            }
          });

          console.log('Populated attributeValues object:', this.attributeValues);
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading task attribute values:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load task attribute values.'
          });
        }
      });
  }

  private parseMultiSelectAttributeValue(value: any): any[] {
    if (Array.isArray(value)) {
      return value;
    }

    if (value === undefined || value === null || value === '') {
      return [];
    }

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [String(value)];
    } catch {
      return [String(value)];
    }
  }

  private hasPendingCommentDraft(): boolean {
    return !!this.newCommentText.trim() || this.selectedCommentFiles.length > 0;
  }

  private requiresCommentForAttachments(): boolean {
    return this.selectedCommentFiles.length > 0 && !this.newCommentText.trim();
  }

  private submitPendingCommentDraft(showSuccessMessage: boolean): Observable<boolean> {
    if (!this.hasPendingCommentDraft()) {
      return of(true);
    }

    if (this.requiresCommentForAttachments()) {
      this.commentValidationError = true;
      return of(false);
    }

    if (!this.isEditMode || !this.editTask?.taskId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Save Task First',
        detail: 'Please save the task before adding comments or attachments.'
      });
      return of(false);
    }

    const currentUser = this.ssoService.getCurrentUser();
    if (!currentUser?.userId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'User not authenticated. Please login again.'
      });
      return of(false);
    }

    const pendingFiles = [...this.selectedCommentFiles];
    const commentText = this.newCommentText.trim();
    const commentModel: TaskCommentCreate = {
      taskId: this.editTask.taskId,
      commentText
    };

    return this.taskManagementService.addTaskComment(commentModel, currentUser.userId).pipe(
      switchMap((response) => this.uploadCommentAttachments$(response.commentId, pendingFiles)),
      map((uploadResult) => {
        this.loadTaskComments();

        if (uploadResult.failedCount > 0) {
          this.selectedCommentFiles = uploadResult.failedFiles;
          this.commentValidationError = false;
          this.messageService.add({
            severity: 'warn',
            summary: 'Partial Success',
            detail: `Comment saved, but ${uploadResult.failedCount} attachment(s) failed to upload.`
          });
          return false;
        }

        this.resetCommentForm();
        if (showSuccessMessage) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: pendingFiles.length > 0
              ? `Comment added with ${pendingFiles.length} attachment(s).`
              : 'Comment added successfully.'
          });
        }
        return true;
      }),
      catchError((error) => {
        console.error('Error adding comment:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to add comment. Please try again.'
        });
        return of(false);
      })
    );
  }

  private uploadCommentAttachments$(
    commentId: number,
    files: File[]
  ): Observable<{ failedCount: number; failedFiles: File[] }> {
    if (files.length === 0) {
      return of({ failedCount: 0, failedFiles: [] });
    }

    return forkJoin(
      files.map((file) =>
        this.taskManagementService.addCommentAttachment(commentId, file).pipe(
          map(() => ({ success: true, file })),
          catchError((error) => {
            console.error('Error uploading attachment:', error);
            return of({ success: false, file });
          })
        )
      )
    ).pipe(
      map((results) => {
        const failedFiles = results.filter((result) => !result.success).map((result) => result.file);
        return {
          failedCount: failedFiles.length,
          failedFiles,
        };
      })
    );
  }

  private resetCommentForm() {
    this.newCommentText = '';
    this.selectedCommentFiles = [];
    this.commentValidationError = false;
    // Clean up preview URLs
    this.selectedFilePreviewUrls.forEach(preview => {
      URL.revokeObjectURL(preview.previewUrl);
    });
    this.selectedFilePreviewUrls = [];
  }
}
